import { Employee } from '../types/EmployeeTypes';
import apiClient from './apiClient';

// --- NUEVA INTERFAZ PARA FRENTES ---
export interface HistoricalFront {
    id: number;
    label: string; // Ejemplo: "Excavación - Frente 1"
}

export const getEmployees = async (): Promise<Employee[]> => {
  const response = await apiClient.get("/Employees");
  return await response.data;
};

export const getEmployeeByRut = async (rut: string): Promise<Employee | null> => {
  try {
    const response = await apiClient.post<Employee>('/Employees/rut', { rut: rut });
    return response.data;
  } catch (error) {
    // Si lanza error (ej. 400/404 porque no existe), retornamos null
    return null;
  }
};

export const getEmployeesByIdProject = async (idProject: number): Promise<Employee[]> => {
  try {
    const response = await apiClient.get<Employee[]>(`/Employees/project/${idProject}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener los empleados del proyecto:', error);
    throw new Error('Error al obtener los empleados del proyecto.');
  }
};

export const addEmployee = async (employee: Omit<Employee, 'id'>): Promise<Employee> => {
  const response = await apiClient.post("/Employees", employee)
  return await response.data;
};

export const updateEmployee = async (employee: Employee, oldRut: string): Promise<void> => {
  await apiClient.put(`Employees/update/${oldRut}`, employee)
};

export const deleteEmployee = async (employeeId: string): Promise<void> => {
  try {
    await apiClient.post('Employees/delete', { rut: employeeId });
    console.log(`Empleado con RUT ${employeeId} eliminado exitosamente`);
  } catch (error) {
    console.error('Error al eliminar el empleado:', error);
    throw new Error('No se pudo eliminar el empleado');
  }
};

export const downloadFormat = async () => {
  try {
    const response = await apiClient.get(`Employees/download-format`,
      { responseType: 'blob' });
    const blob = await response.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Empleados-formato.xlsx';
    a.click();
  } catch (error) {
    console.error('Error al descargar el formato:', error);
  }
};

export interface ImportResult {
    importados: number;
    fallidos: number;
    errores: string[];
    mensaje: string;
}

export const importEmployees = async (file: File): Promise<ImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<ImportResult>(`Employees/import`, formData);
    return response.data;
};

export const exportEmployees = async () => {
  try {
    const response = await apiClient.get(`Employees/export`, { responseType: 'blob' });
    const blob = await response.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Empleados.xlsx';
    a.click();
  } catch (error) {
    console.error('Error al exportar empleados:', error);
  }
};

/**
 * Personal que FICHÓ (marca de entrada) en una fecha y cuyo CC asignado por geocerca coincide con el del
 * proyecto. Es el pool de personal para asignar en el reporte diario: quien no fichó, no aparece.
 * Mapea el snapshot de personal al shape {@link Employee} que consume el formulario.
 */
export const getFichadosByDate = async (idProject: number, date: string): Promise<Employee[]> => {
  const response = await apiClient.get('/buk/personal/fichados', {
    params: { projectId: idProject, date },
  });
  return (response.data || []).map((s: any) => ({
    rut: s.rut,
    name: s.name,
    firstName: s.firstName,
    lastName: s.lastName,
    position: s.position,
    status: s.status,
  }));
};

// 1. Obtener empleados de una fecha histórica
export const getEmployeesByReportDate = async (idProject: number, date: string): Promise<Employee[]> => {
  const formattedDate = date.includes('T') ? date : `${date}T00:00:00`;
  const response = await apiClient.get<Employee[]>(`/Daily-report-employees/historical/list`, {
    params: { projectId: idProject, date: formattedDate }
  });
  return response.data;
};

// 2. Obtener frentes disponibles para el combo
export const getHistoricalFronts = async (idProject: number, date: string): Promise<HistoricalFront[]> => {
  const formattedDate = date.includes('T') ? date : `${date}T00:00:00`;
  const response = await apiClient.get<HistoricalFront[]>(`/Daily-report-employees/historical/fronts`, {
    params: { projectId: idProject, date: formattedDate }
  });
  return response.data;
};

// 3. Agregar empleado al reporte histórico con ID de FRENTE
export const addEmployeeToReport = async (
    idProject: number,
    date: string,
    employee: Omit<Employee, 'id'>,
    workFrontId: number // <--- Ahora recibe el frente
): Promise<void> => {
  const formattedDate = date.includes('T') ? date : `${date}T00:00:00`;
  await apiClient.post(`/Daily-report-employees/historical/add`, employee, {
    params: { projectId: idProject, date: formattedDate, workFrontId: workFrontId }
  });
};

// 4. Quitar empleado del reporte histórico
export const removeEmployeeFromReport = async (idProject: number, date: string, rut: string): Promise<void> => {
  const formattedDate = date.includes('T') ? date : `${date}T00:00:00`;
  await apiClient.delete(`/Daily-report-employees/historical/remove`, {
    params: { projectId: idProject, date: formattedDate, rut: rut }
  });
};

export const getEmployeesFromBuk = async (): Promise<any> => {
  try {
    const response = await apiClient.get('buk/employees');
    return response.data;
  } catch (error) {
    console.error('Error al sincronizar con Buk:', error);
    throw new Error('No se pudo conectar con la API de Buk.');
  }
};

// --- Histórico de personal de Buk (snapshots diarios guardados a las 15:00) ---

// Fechas disponibles con snapshot histórico (más reciente primero)
export const getBukPersonalHistoryDates = async (): Promise<string[]> => {
  try {
    const response = await apiClient.get<string[]>('/buk/personal/history/dates');
    return response.data;
  } catch (error) {
    console.error('Error al obtener las fechas del histórico de personal:', error);
    throw new Error('No se pudieron obtener las fechas del histórico.');
  }
};

// Personal registrado en una fecha histórica específica (formato YYYY-MM-DD)
export const getBukPersonalHistoryByDate = async (date: string): Promise<any[]> => {
  try {
    const response = await apiClient.get('/buk/personal/history', { params: { date } });
    return response.data;
  } catch (error) {
    console.error('Error al obtener el histórico de personal:', error);
    throw new Error('No se pudo obtener el histórico de personal.');
  }
};

// Dispara manualmente la sincronización con Buk y guarda el snapshot de hoy
export const syncBukPersonalNow = async (): Promise<any[]> => {
  try {
    const response = await apiClient.post('/buk/personal/history/sync-now');
    return response.data;
  } catch (error) {
    console.error('Error al sincronizar manualmente con Buk:', error);
    throw new Error('No se pudo sincronizar con Buk.');
  }
};

// Inserta un nuevo registro histórico de personal
export const createBukPersonalRecord = async (record: any): Promise<any> => {
  const response = await apiClient.post('/buk/personal/history', record);
  return response.data;
};

// Actualiza un registro histórico existente por id
export const updateBukPersonalRecord = async (id: number, record: any): Promise<any> => {
  const response = await apiClient.put(`/buk/personal/history/${id}`, record);
  return response.data;
};

// Elimina un registro histórico por id
export const deleteBukPersonalRecord = async (id: number): Promise<void> => {
  await apiClient.delete(`/buk/personal/history/${id}`);
};

export const getAttendanceData = async (desde: string, hasta: string): Promise<any[]> => {
  try {
    const response = await apiClient.get('/buk/attendance', { params: { desde, hasta } });
    return response.data;
  } catch (error) {
    console.error('Error al obtener asistencia:', error);
    throw new Error('No se pudo conectar con la API de asistencia.');
  }
};